import { api } from './client'

export interface MessageTemplate {
  id: string
  channel: string
  name: string
  subject: string | null
  body: string
  fromTemplate: boolean
}

/** An email attachment referenced by its stored key (from POST /api/files). */
export interface AttachmentInput {
  key: string
  filename: string
  contentType: string | null
}

export interface SendMessageInput {
  channel: string
  templateId?: string | null
  subject?: string | null
  body?: string | null
  /** Email attachments; ignored for SMS. */
  attachments?: AttachmentInput[]
  /** False leaves the sender's signature off this email (default: on). */
  includeSignature?: boolean
}

/** Mirrors backend MessageTemplateRequest. subject only applies to the email channel. */
export interface MessageTemplateInput {
  channel: 'email' | 'sms'
  name: string
  subject?: string | null
  body: string
}

export async function listTemplates(channel?: string): Promise<MessageTemplate[]> {
  const { data } = await api.get<MessageTemplate[]>('/api/message-templates', { params: { channel } })
  return data
}

export async function createTemplate(input: MessageTemplateInput): Promise<MessageTemplate> {
  const { data } = await api.post<MessageTemplate>('/api/message-templates', input)
  return data
}

export async function updateTemplate(id: string, input: MessageTemplateInput): Promise<MessageTemplate> {
  const { data } = await api.put<MessageTemplate>(`/api/message-templates/${id}`, input)
  return data
}

export async function deleteTemplate(id: string): Promise<void> {
  await api.delete(`/api/message-templates/${id}`)
}

export async function sendDealMessage(dealId: string, input: SendMessageInput): Promise<void> {
  await api.post(`/api/deals/${dealId}/send`, input)
}

/** Send one message to a single contact directly. 400 `consent.blocked` if they opted out. */
export async function sendContactMessage(contactId: string, input: SendMessageInput): Promise<void> {
  await api.post(`/api/contacts/${contactId}/send`, input)
}

/** Where a message would go — a deal (to its primary contact, from its owner) or one contact. */
export type PreviewTarget = { kind: 'deal' | 'contact'; id: string }

/** A message rendered by the backend exactly as sending would — what the recipient actually gets. */
export interface MessagePreview {
  channel: string
  fromName: string | null
  fromAddress: string | null
  toName: string | null
  to: string | null
  subject: string | null
  /** Email: sanitized HTML incl. signature and footer. SMS: plain text incl. the STOP line. */
  body: string
  /** Why it couldn't go out as is (no recipient, opted out), else null. */
  notice: string | null
}

export async function previewMessage(target: PreviewTarget, input: SendMessageInput): Promise<MessagePreview> {
  const path = target.kind === 'deal' ? `/api/deals/${target.id}/send/preview` : `/api/contacts/${target.id}/send/preview`
  const { data } = await api.post<MessagePreview>(path, input)
  return data
}

/** "Send me a test": the email as this contact would get it, to your own inbox only. Returns where it went. */
export async function sendTestToMe(contactId: string, input: SendMessageInput): Promise<string> {
  const { data } = await api.post<{ sentTo: string }>(`/api/contacts/${contactId}/send/test`, input)
  return data.sentTo
}

export interface BulkSendInput extends SendMessageInput {
  contactIds: string[]
}

/**
 * Contacts opted out of the channel are skipped and counted, not failed. A large campaign is queued
 * to a background worker and comes back with `queued` = the recipient count (sent/skipped then 0);
 * a small one reports exact `sent`/`skipped` with `queued` 0.
 */
export interface BulkSendResult {
  sent: number
  skipped: number
  queued: number
}

export async function bulkSendToContacts(input: BulkSendInput): Promise<BulkSendResult> {
  const { data } = await api.post<BulkSendResult>('/api/contacts/send', input)
  return data
}
